import { ReportData, EncryptedData, BatchReportData, InvalidEntryReport } from "~/types/global";
import { REPORT_CONSTANTS } from "~/helpers/constants";
import { FolderData } from "root/domain/entities/folder.model";
import { ReportModel } from "root/domain/entities/report.model";
import { FolderModel } from "root/domain/entities/folder.model";
import { BatchModel } from "root/domain/entities/batch.model";
import { DisplayableItem } from "root/domain/entities/base.model";

export type ListItem = ReportData | FolderData | BatchReportData;

export class ListReportOutputDTO {
  private readonly items: ListItem[];
  private readonly reportModels: ReportModel[];
  private readonly folderModels: FolderModel[];
  private readonly batchModels: BatchModel[];

  constructor(data: unknown) {
    this.items = this.validateAndTransform(data);
    const reports = this.items.filter(item => this.isValidReportData(item));
    const folders = this.items.filter(item => this.isValidFolderData(item));
    const batches = this.items.filter(item => this.isValidBatchData(item));
    this.reportModels = reports.map(r => new ReportModel(r));
    this.folderModels = folders.map(f => new FolderModel(f));
    this.batchModels = batches.map(b => new BatchModel(b));
  }

  private validateAndTransform(data: unknown): ListItem[] {
    if (!Array.isArray(data)) {
      console.error("Invalid data format for list: expected an array");
      throw new Error("Erro ao buscar lista de itens.");
    }

    return data?.map((item, index) => {
      if (this.isValidReportData(item)) {
        return item;
      }
      if (this.isValidFolderData(item)) {
        return item;
      }
      if (this.isValidBatchData(item)) {
        return item;
      }
      console.error(`Invalid data at index ${index} on the list`, item);
      throw new Error("Erro ao buscar lista de itens.");
    });
  }

  private isValidReportData(item: any): item is ReportData {
    const { new_report } = REPORT_CONSTANTS;
    const hasKeys = [
      new_report.report_id,
      new_report.report_status,
      new_report.report_type,
      new_report.report_search_args,
    ].every(k => k in item);
    if (!hasKeys) return false;

    if (typeof item[new_report.report_id] !== "string") return false;

    // TODO - Remover depois de resolvido problemas search args retornando normal no card pending
    const isPending = typeof item[new_report.report_status] === "object" &&
      item[new_report.report_status]?.status_report === "pending";
    if (!isPending && !this.isValidEncryptedData(item[new_report.report_search_args])) {
      return false;
    }

    const rt = item[new_report.report_type];
    if (typeof rt !== "string" && !this.isValidEncryptedData(rt)) return false;

    return true;
  }

  private isValidFolderData(item: any): item is FolderData {
    const required = [
      "folder_id",
      "folder_name",
      "folder_path",
      "depth_level",
      "created_at",
      "modified_at",
    ] as const;

    if (!required.every(k => k in item)) return false;
    if (typeof item.folder_id !== "string") return false;
    if (!this.isValidEncryptedData(item.folder_name)) return false;
    if (typeof item.folder_path !== "string") return false;
    if (typeof item.depth_level !== "number") return false;

    return true;
  }

  private isValidBatchData(item: any): item is BatchReportData {
    const required = [
      "batch_id",
      "status_code",
      "total",
      "done",
      "failed",
      "remaining",
    ] as const;

    if (!required.every(k => k in item)) return false;
    if (typeof item.batch_id !== "string") return false;
    if (typeof item.status_code !== "string") return false;
    if (typeof item.total !== "number") return false;
    if (typeof item.done !== "number") return false;
    if (typeof item.failed !== "number") return false;
    if (typeof item.remaining !== "number") return false;

    // Validar invalid_entries_report_list se presente
    if (item.invalid_entries_report_list !== undefined) {
      if (!Array.isArray(item.invalid_entries_report_list)) return false;
      for (const entry of item.invalid_entries_report_list) {
        if (!this.isValidInvalidEntryReport(entry)) return false;
      }
    }

    return true;
  }

  private isValidInvalidEntryReport(entry: any): entry is InvalidEntryReport {
    if (!entry || typeof entry !== "object") return false;
    if (typeof entry.report_type !== "string") return false;
    if (!entry.report_search_args || typeof entry.report_search_args !== "object") return false;

    // Aceitar formato criptografado (iv + encrypted) ou não criptografado (ex: {cpf: '123'})
    const searchArgs = entry.report_search_args;
    const isEncrypted = typeof searchArgs.iv === "string" && typeof searchArgs.encrypted === "string";
    const isPlainObject = Object.keys(searchArgs).length > 0;

    return isEncrypted || isPlainObject;
  }

  private isValidEncryptedData(data: any): data is EncryptedData {
    return (
      data != null &&
      typeof data.encrypted === "string" &&
      typeof data.iv === "string"
    );
  }

  public getReports(): ReadonlyArray<ReportData> {
    return this.items.filter(item => this.isValidReportData(item));
  }

  public getFolders(): ReadonlyArray<FolderData> {
    return this.items.filter(item => this.isValidFolderData(item));
  }

  public getBatches(): ReadonlyArray<BatchReportData> {
    return this.items.filter(item => this.isValidBatchData(item));
  }

  public getAllItems(): ReadonlyArray<ListItem> {
    return this.items;
  }

  public getReportModels(): ReadonlyArray<ReportModel> {
    return this.reportModels;
  }

  public getFolderModels(): ReadonlyArray<FolderModel> {
    return this.folderModels;
  }

  public getBatchModels(): ReadonlyArray<BatchModel> {
    return this.batchModels;
  }

  public getAllModels(): ReadonlyArray<DisplayableItem> {
    return [...this.reportModels, ...this.folderModels, ...this.batchModels];
  }

  public countReports(): number {
    return this.getReports().length;
  }

  public countFolders(): number {
    return this.getFolders().length;
  }

  public countBatches(): number {
    return this.getBatches().length;
  }

  public count(): number {
    return this.items.length;
  }
}